import {
  faCirclePlus,
  faInfoCircle,
  faTags,
  faUpload,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button, Modal } from '@orobix/orobix-fe-components'
import { useEffect, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'

import { Dataset, OPTIMIZATION_ORDERS } from '../../../types/dataset'
import {
  isDone,
  OptimizationProcess,
  OptimizationProcesses,
  OptimizationProcessStatus,
} from '../../../types/optimizationProcesses'
import { get, post } from '../../../utils/api'
import { useStyles } from './WizardButton.styles'

// Custom Tooltip Component
const CustomTooltip = ({ children }: { children: React.ReactNode }) => {
  const [isVisible, setIsVisible] = useState(false)
  const tooltipRef = useRef<HTMLDivElement>(null)
  const { t } = useTranslation()
  const classes = useStyles()

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        tooltipRef.current &&
        !tooltipRef.current.contains(event.target as Node)
      ) {
        setIsVisible(false)
      }
    }

    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isVisible])

  return (
    <div className={classes.tooltipWrapper} ref={tooltipRef}>
      <div
        role="button"
        tabIndex={0}
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        onClick={() => setIsVisible(!isVisible)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            setIsVisible(!isVisible)
          }
        }}
      >
        {children}
      </div>
      {isVisible && (
        <div className={classes.tooltipContent}>
          <div className={classes.tooltipText}>
            {t('Zip archive tooltip text', {
              sheetName: t('START_END_PROJECT sheet name'),
            })}
          </div>
          <div className={classes.tooltipImageWrapper}>
            <img
              src="/sample_curves.png"
              alt={t('Sample curves example')}
              className={classes.tooltipImage}
            />
          </div>
          <div className={classes.tooltipArrow} />
        </div>
      )}
    </div>
  )
}

export const WizardButton = ({
  optimizationProcessId,
  status,
  version,
  optimizationProcessType,
}: {
  optimizationProcessId: number
  status: OptimizationProcessStatus
  version?: string
  optimizationProcessType?: string
}) => {
  const { t } = useTranslation()
  const classes = useStyles()
  const [modal, setModal] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [processName, setProcessName] = useState('')
  const [processNotes, setProcessNotes] = useState('')

  // Get the latest dataset
  const { data: latestDataset } = useQuery<Dataset>(
    ['data-ingestions', 'latest'],
    () => get<Dataset>('/data-ingestions/latest/'),
    {
      retry: false,
    }
  )

  // Get optimization processes that reference this one to check for higher versions
  const { data: referencedProcessesResponse } = useQuery<OptimizationProcesses>(
    ['optimization-processes', 'referenced-by', optimizationProcessId],
    () => get<OptimizationProcesses>(`/optimization-processes/?limit=100`),
    {
      retry: false,
      enabled: !!optimizationProcessId, // Only run if we have an optimizationProcessId
    }
  )

  const handleCloseModal = () => {
    setSelectedFile(null)
    setProcessName('')
    setProcessNotes('')
    setModal(false)
  }

  const handleManualUpload = () => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = '.zip'
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0]
      if (file) {
        setSelectedFile(file)
      }
    }
    input.click()
  }

  const { mutate: createOptimizationProcess, isLoading: isCreating } =
    useCreateOptimizationProcess(
      handleCloseModal,
      latestDataset?.id ? String(latestDataset.id) : '',
      selectedFile,
      optimizationProcessId,
      processName,
      processNotes
    )

  const incrementVersion = (currentVersion?: string): string => {
    if (!currentVersion) return 'V1'

    // Extract the number from version string (e.g., "V0" -> "0", "V1" -> "1")
    const match = currentVersion.match(/^V(\d+)$/)
    if (match) {
      const versionNumber = parseInt(match[1], 10)
      const nextVersion = versionNumber + 1
      return `V${nextVersion}`
    }

    // Fallback if version format is unexpected
    return 'V1'
  }

  // Extract version number for comparison
  const getVersionNumber = (versionString?: string): number => {
    if (!versionString) return 0
    const match = versionString.match(/^V(\d+)$/)
    return match ? parseInt(match[1], 10) : 0
  }

  // Check if there are any processes referencing this one with a higher version
  const currentVersionNumber = getVersionNumber(version)
  const hasNewerVersion =
    referencedProcessesResponse?.results?.some(
      (process) =>
        process.referenceOptimizationProcess === optimizationProcessId &&
        getVersionNumber(process.version) > currentVersionNumber
    ) ?? false

  const nextVersion = incrementVersion(version)

  const handleWizardClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setModal(true)
  }

  const isCompleted = isDone(status)
  const isOrdersType = optimizationProcessType === OPTIMIZATION_ORDERS
  const isDisabled = !isCompleted || !isOrdersType || hasNewerVersion

  return (
    <>
      <Button
        icon={<FontAwesomeIcon icon={faCirclePlus} />}
        onClick={handleWizardClick}
        title={t('Create new version')}
        label=""
        buttonType="button"
        type="blank-action"
        size="md"
        disabled={isDisabled}
      />

      {modal && (
        <Modal
          title={t('New version of optimization process')}
          onCloseRequest={handleCloseModal}
          footer={
            <>
              <div className={classes.buttonContainer}>
                <Button
                  icon={<FontAwesomeIcon icon={faUpload} />}
                  label={t('New data from zip')}
                  type="action"
                  onClick={handleManualUpload}
                  disabled={isCreating}
                />
              </div>
              {selectedFile && (
                <div className={classes.selectionContainer}>
                  <p className={classes.selectionDescription}>
                    <Trans
                      i18nKey="Create the new version <newVersion>{{ newVersion }}</newVersion> of optimization process <oldVersion>{{ oldVersion }}</oldVersion> based on uploaded zip file <name>{{ fileName }}</name>?"
                      values={{
                        fileName: selectedFile.name,
                        newVersion: nextVersion,
                        oldVersion: version || '-',
                      }}
                      components={{
                        newVersion: <strong />,
                        oldVersion: <strong />,
                        name: <strong />,
                      }}
                    />
                  </p>
                  <Button
                    icon={<FontAwesomeIcon icon={faTags} />}
                    label={t('Create')}
                    type="action"
                    onClick={() => {
                      if (latestDataset?.id) {
                        createOptimizationProcess()
                      }
                    }}
                    loading={isCreating}
                    disabled={isCreating || !latestDataset?.id}
                  />
                </div>
              )}
            </>
          }
        >
          <div className={classes.modalContent}>
            <p>
              <Trans
                i18nKey="Create a new version of optimization process based on uploaded zip file containing data from the existing optimization process of id <id>{{ processId }}</id> and version <version>{{ processVersion }}</version>."
                values={{
                  processId: optimizationProcessId,
                  processVersion: version || '-',
                }}
                components={{
                  id: <strong />,
                  version: <strong />,
                }}
              />
            </p>
            <p>
              <Trans
                i18nKey="A new optimization process of version <newVersion>{{ processVersion }}</newVersion> will be created and will be available to be selected for a new optimization process, clicking <button>'New'</button> button on top right of screen"
                values={{
                  processVersion: nextVersion,
                }}
                components={{
                  newVersion: <strong />,
                  button: <strong />,
                }}
              />
            </p>
            <div className={classes.fieldWrapper}>
              <label htmlFor="wizardProcessName" className={classes.fieldLabel}>
                {t('Optimization process name (optional)')}
              </label>
              <input
                id="wizardProcessName"
                type="text"
                maxLength={256}
                value={processName}
                onChange={(e) => setProcessName(e.target.value)}
                className={classes.textInput}
              />
            </div>
            <div className={classes.fieldWrapper}>
              <label
                htmlFor="wizardProcessNotes"
                className={classes.fieldLabel}
              >
                {t('Notes')}
              </label>
              <textarea
                id="wizardProcessNotes"
                value={processNotes}
                onChange={(e) => setProcessNotes(e.target.value)}
                rows={3}
                className={classes.textarea}
              />
            </div>
            <p>
              <strong>{t('Prerequisites:')}</strong>
            </p>
            <ul>
              <li>
                <Trans
                  i18nKey="Download '<fileName>results_{{ fileName }}.zip</fileName>' from optimization process of id <id>{{ processId }}</id>"
                  values={{
                    fileName: optimizationProcessId,
                    processId: optimizationProcessId,
                  }}
                  components={{
                    fileName: <strong />,
                    id: <strong />,
                  }}
                />
              </li>
              <li>
                <Trans
                  i18nKey="edit and apply changes to its 'data_from_user.xlsx' file"
                  components={{
                    fileName: <strong />,
                    folder: <strong />,
                  }}
                />
                <CustomTooltip>
                  <FontAwesomeIcon
                    icon={faInfoCircle}
                    className={classes.tooltipIcon}
                  />
                </CustomTooltip>
              </li>
              <li>
                {t(
                  "upload modified zip by clicking on 'New data from zip' button"
                )}
              </li>
            </ul>
          </div>
        </Modal>
      )}
    </>
  )
}

const useCreateOptimizationProcess = (
  setModal: (value: boolean) => void,
  datasetId: string,
  fileInputs?: File | null,
  referenceOptimizationProcessId?: number,
  name?: string,
  notes?: string
) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  return useMutation(
    ['optimization-processes', 'create'],
    () => {
      if (!datasetId || datasetId === '' || datasetId === 'undefined') {
        throw new Error('No valid dataset available')
      }

      const formData = new FormData()
      formData.append('data_ingestion_id', datasetId)
      formData.append('optimization_type', OPTIMIZATION_ORDERS)

      if (fileInputs) {
        formData.append('file_inputs', fileInputs)
      }

      if (referenceOptimizationProcessId) {
        formData.append(
          'reference_optimization_process',
          String(referenceOptimizationProcessId)
        )
      }

      if (name) {
        formData.append('name', name)
      }

      if (notes) {
        formData.append('notes', notes)
      }

      return post<OptimizationProcess>(
        '/optimization-processes/',
        formData,
        undefined,
        true // payloadIsNotAnObject - skip snakeCaseKeys transformation
      )
    },
    {
      retry: false,
      onSuccess: () => {
        queryClient.invalidateQueries(['optimization-processes'])

        toast.success(
          t('A new optimization process has been added to the processing queue')
        )

        setModal(false)
      },
      onError: (error: unknown) => {
        let errorMessage = t(
          'An error occurred. Please try again later or contact the support.'
        )
        if (error && typeof error === 'object') {
          const err = error as {
            response?: {
              status?: number
              data?: {
                error?: string
                detail?: string
                message?: string
                non_field_errors?: string[]
                [key: string]: unknown
              }
            }
            message?: string
          }

          const { response, message } = err

          // For 5xx errors, show generic message (avoid HTML error pages)
          if (response?.status && response.status >= 500) {
            errorMessage = t(
              'A server error occurred. Please try again later or contact the support.'
            )
          } else if (response?.data) {
            // For 4xx errors, try to extract detailed error message
            const {
              error: backendError,
              detail,
              message: dataMessage,
              non_field_errors: nonFieldErrors,
              ...otherFields
            } = response.data
            // Check for common error message fields
            errorMessage =
              backendError ||
              detail ||
              dataMessage ||
              // Handle validation errors (array of messages)
              (Array.isArray(nonFieldErrors)
                ? nonFieldErrors.join(', ')
                : '') ||
              // Handle field-specific errors
              (Object.keys(otherFields).length > 0
                ? Object.entries(otherFields)
                    .filter(([key]) => key !== '__all__')
                    .map(([, value]) =>
                      Array.isArray(value) ? value.join(', ') : String(value)
                    )
                    .join('; ')
                : '') ||
              message ||
              errorMessage
          } else if (message) {
            errorMessage = message
          }
        }
        toast.error(errorMessage, { autoClose: false })
        setModal(false)
      },
    }
  )
}
